import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, ModalContent, ModalHeader, ModalTitle, ModalDescription, ModalBody, ModalFooter, useToast } from '../sectepe-ui/index';
import crmService, { Deal, PipelineStage, PipelineSummary } from '../services/crmService';
import quoteService from '../services/quoteService';
import taskService from '../services/taskService';
import { PageHeader, EmptyState, LoadingState, StatusBadge, useConfirm, RelatedLinks, type RelatedLinkItem } from '../components/ui';
import { useKampagnenZuordnung, KampagneVerweis } from '../vertrieb/kampagnen';
import { Icon } from '../components/icons';
import { SalesProcessBar } from '../vertrieb/SalesProcessBar';
import { KpiRow } from '../vertrieb/KpiRow';
import { euro, datum, faelligkeit, istUeberfaellig, isoTag, fehlerText, zeit } from '../vertrieb/format';
import { RechteHinweis } from '../vertrieb/RechteHinweis';
import { useBerechtigung } from '../berechtigungen/useBerechtigung';

/** Aufgaben, Angebote, Auftraege und Rechnungen eines Vorgangs als Chips. */
function vorgangsVerweise(d: Deal): RelatedLinkItem[] {
  const enc = encodeURIComponent;
  return [
    ...(d.tasks ?? []).map((t) => ({
      key: `task-${t.id}`, label: t.title, icon: 'check' as const, to: `/aufgaben?aufgabe=${enc(t.id)}`,
      done: t.status !== 'open', title: t.status === 'open' ? 'Offene Aufgabe öffnen' : 'Erledigte Aufgabe',
    })),
    ...(d.quotes ?? []).map((q) => ({
      key: `quote-${q.id}`, label: `Angebot ${q.number}`, icon: 'file-check' as const, to: `/angebote?angebot=${enc(q.id)}`, title: 'Angebot öffnen',
    })),
    ...(d.orders ?? []).map((o) => ({
      key: `order-${o.id}`, label: `Auftrag ${o.number || '(Entwurf)'}`, icon: 'package' as const, to: `/auftraege?auftrag=${enc(o.id)}`, title: 'Auftrag öffnen',
    })),
    ...(d.invoices ?? []).map((r) => ({
      key: `inv-${r.id}`, label: `${r.art === 'gutschrift' ? 'Gutschrift' : 'Rechnung'} ${r.number || '(Entwurf)'}`, icon: 'receipt' as const,
      to: `/rechnungen?rechnung=${enc(r.id)}`, title: 'Rechnung öffnen',
    })),
  ];
}

/** Deutsche Namen fuer die Notiz-Arten aus dem Backend. */
const NOTIZ_ART: Record<string, string> = {
  note: 'Notiz',
  stage_change: 'Stufe',
  email: 'E-Mail',
  call: 'Telefonat',
  meeting: 'Termin',
};

/**
 * Vertriebspipeline. Vorgaenge lassen sich per Drag-and-drop zwischen den
 * Stufen ziehen; ein Vorgang oeffnet sich ueber ?deal=<id>, damit Akte,
 * Aufgaben und Cockpit direkt hierher verlinken koennen.
 *
 * Welle 2: Anlegen, Ziehen, Notizen und Felder verlangen vertrieb.bearbeiten,
 * Loeschen vertrieb.loeschen, "Angebot erstellen" angebot.bearbeiten. Ohne
 * Schreibrecht (Buchhaltung) bleibt das Brett lesbar, aber nichts zieht.
 */
const PipelinePage: React.FC = () => {
  const navigate = useNavigate();
  const { push } = useToast();
  const { confirm, prompt } = useConfirm();
  const { darf } = useBerechtigung();
  const zuordnung = useKampagnenZuordnung();
  const darfBearbeiten = darf('vertrieb.bearbeiten');
  const darfLoeschen = darf('vertrieb.loeschen');
  const darfAngebot = darf('angebot.bearbeiten');
  const [params, setParams] = useSearchParams();
  const dealParam = params.get('deal');

  const [stufen, setStufen] = useState<PipelineStage[]>([]);
  const [vorgaenge, setVorgaenge] = useState<Deal[]>([]);
  const [kennzahlen, setKennzahlen] = useState<PipelineSummary | null>(null);
  const [laedt, setLaedt] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [gezogen, setGezogen] = useState<string | null>(null);
  const [zielStufe, setZielStufe] = useState<string | null>(null);

  const [offenerVorgang, setOffenerVorgang] = useState<Deal | null>(null);
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);

  const [zeigeNeu, setZeigeNeu] = useState(false);
  const [neuTitel, setNeuTitel] = useState('');
  const [neuFirma, setNeuFirma] = useState('');
  const [neuWert, setNeuWert] = useState('');

  const laden = useCallback(async () => {
    setFehler(null);
    try {
      const [s, d, k] = await Promise.all([
        crmService.getStages(),
        crmService.getDeals({ status: 'open' }),
        crmService.getSummary(),
      ]);
      setStufen(s ?? []);
      setVorgaenge(d ?? []);
      setKennzahlen(k ?? null);
    } catch (e) {
      setFehler(fehlerText(e, 'Laden fehlgeschlagen'));
    } finally {
      setLaedt(false);
    }
  }, []);

  useEffect(() => { laden(); }, [laden]);

  // Deep-Link ?deal=<id>: den Vorgang oeffnen, sobald die Seite steht.
  useEffect(() => {
    if (!dealParam) {
      setOffenerVorgang(null);
      return;
    }
    let aktiv = true;
    Promise.resolve()
      .then(() => crmService.getDeal(dealParam))
      .then((d) => { if (aktiv && d) setOffenerVorgang(d); })
      .catch((e) => { if (aktiv) setFehler(fehlerText(e, 'Vorgang konnte nicht geladen werden')); });
    return () => { aktiv = false; };
  }, [dealParam]);

  const oeffnen = (deal: Deal) => {
    const next = new URLSearchParams(params);
    next.set('deal', deal.id);
    setParams(next);
  };

  const schliessen = () => {
    const next = new URLSearchParams(params);
    next.delete('deal');
    setParams(next, { replace: true });
    setOffenerVorgang(null);
  };

  const neuLaden = async (id: string) => {
    setOffenerVorgang(await crmService.getDeal(id));
    await laden();
  };

  const verschieben = async (dealId: string, stageId: string) => {
    if (!darfBearbeiten) return;
    const vorher = vorgaenge;
    // Sofort umsetzen, damit die Karte nicht springt; bei Fehler zuruecksetzen.
    setVorgaenge((alt) => alt.map((d) => (d.id === dealId ? { ...d, stageId } : d)));
    try {
      await crmService.updateDeal(dealId, { stageId });
      await laden();
    } catch (e) {
      setVorgaenge(vorher);
      setFehler(fehlerText(e, 'Verschieben fehlgeschlagen'));
    }
  };

  const anlegen = async () => {
    if (!neuTitel.trim()) return;
    setBusy(true);
    try {
      const deal = await crmService.createDeal({
        title: neuTitel.trim(),
        company: neuFirma.trim() || undefined,
        value: neuWert ? Number(neuWert) : 0,
      });
      setNeuTitel('');
      setNeuFirma('');
      setNeuWert('');
      setZeigeNeu(false);
      push({ message: `Vorgang „${deal.title}“ angelegt.`, severity: 'success' });
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Anlegen fehlgeschlagen'));
    } finally {
      setBusy(false);
    }
  };

  const notizSpeichern = async () => {
    if (!offenerVorgang || !notiz.trim()) return;
    try {
      await crmService.addNote(offenerVorgang.id, notiz.trim());
      setNotiz('');
      await neuLaden(offenerVorgang.id);
    } catch (e) {
      setFehler(fehlerText(e, 'Notiz konnte nicht gespeichert werden'));
    }
  };

  const feldSpeichern = async (feld: string, wert: any) => {
    if (!offenerVorgang) return;
    try {
      await crmService.updateDeal(offenerVorgang.id, { [feld]: wert } as any);
      await neuLaden(offenerVorgang.id);
    } catch (e) {
      setFehler(fehlerText(e, 'Speichern fehlgeschlagen'));
    }
  };

  const abschliessen = async (gewonnen: boolean) => {
    if (!offenerVorgang) return;
    const ziel = stufen.find((s) => (gewonnen ? s.isWon : s.isLost));
    if (!ziel) {
      setFehler(gewonnen ? 'Es gibt keine „Gewonnen“-Stufe.' : 'Es gibt keine „Verloren“-Stufe.');
      return;
    }
    if (gewonnen) {
      const ok = await confirm({
        title: 'Vorgang als gewonnen abschließen?',
        message: `„${offenerVorgang.title}“ wandert in die Stufe „${ziel.name}“ und zählt mit ${euro(offenerVorgang.value, offenerVorgang.currency)} als gewonnen.`,
        confirmLabel: 'Gewonnen',
      });
      if (!ok) return;
      await feldSpeichern('stageId', ziel.id);
      push({ message: 'Glückwunsch – Vorgang gewonnen.', severity: 'success' });
      return;
    }
    const grund = await prompt({
      title: 'Vorgang als verloren abschließen',
      label: 'Grund (optional)',
      placeholder: 'z. B. Budget gestrichen, Wettbewerber',
      multiline: true,
      confirmLabel: 'Verloren',
      tone: 'danger',
    });
    if (grund === null) return;
    try {
      await crmService.updateDeal(offenerVorgang.id, { stageId: ziel.id, lostReason: grund || undefined });
      push({ message: 'Vorgang als verloren abgeschlossen.', severity: 'info' });
      schliessen();
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Abschließen fehlgeschlagen'));
    }
  };

  const loeschen = async () => {
    if (!offenerVorgang) return;
    const ok = await confirm({
      title: 'Vorgang löschen?',
      message: 'Notizen und Verlauf gehen verloren. Zum Abschließen lieber „Verloren“ wählen.',
      confirmLabel: 'Löschen',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await crmService.deleteDeal(offenerVorgang.id);
      push({ message: 'Vorgang gelöscht.', severity: 'success' });
      schliessen();
      await laden();
    } catch (e) {
      setFehler(fehlerText(e, 'Löschen fehlgeschlagen'));
    }
  };

  const angebotErstellen = async () => {
    if (!offenerVorgang) return;
    setBusy(true);
    try {
      const q = await quoteService.create({
        dealId: offenerVorgang.id,
        companyId: offenerVorgang.companyId || undefined,
        title: offenerVorgang.title,
      });
      push({ message: `Angebot ${q.number} zum Vorgang angelegt.`, severity: 'success' });
      navigate(`/angebote?angebot=${q.id}`);
    } catch (e) {
      setFehler(fehlerText(e, 'Angebot konnte nicht angelegt werden'));
    } finally {
      setBusy(false);
    }
  };

  const aufgabeAnlegen = async () => {
    if (!offenerVorgang) return;
    const titel = await prompt({
      title: 'Aufgabe zu diesem Vorgang',
      label: 'Was ist zu tun?',
      defaultValue: offenerVorgang.nextStep || '',
      required: true,
      confirmLabel: 'Anlegen',
    });
    if (!titel) return;
    try {
      await taskService.create({
        title: titel.trim(),
        dealId: offenerVorgang.id,
        companyId: offenerVorgang.companyId || undefined,
        dueAt: offenerVorgang.nextStepAt || new Date().toISOString(),
      });
      push({ message: 'Aufgabe angelegt – steht unter „Heute“.', severity: 'success' });
    } catch (e) {
      setFehler(fehlerText(e, 'Aufgabe konnte nicht angelegt werden'));
    }
  };

  const offeneStufen = stufen.filter((s) => !s.isWon && !s.isLost);
  const abschlussStufen = stufen.filter((s) => s.isWon || s.isLost);

  return (
    <div className="page-container-full">
      <SalesProcessBar stepId="pipeline" />
      <PageHeader
        title="Vertriebspipeline"
        subtitle={darfBearbeiten
          ? 'Vorgänge je Stufe. Karten lassen sich zwischen den Spalten ziehen; jeder Stufenwechsel wird im Verlauf festgehalten.'
          : 'Vorgänge je Stufe; jeder Stufenwechsel wird im Verlauf festgehalten.'}
        actions={darfBearbeiten ? (
          <button type="button" className="btn btn-primary" onClick={() => setZeigeNeu((v) => !v)}>
            <Icon name="plus" size={14} /> Neuer Vorgang
          </button>
        ) : undefined}
      />
      <RechteHinweis was="Vorgänge anlegen, verschieben und bearbeiten" />

      {kennzahlen && (
        <KpiRow
          aria-label="Pipeline-Kennzahlen"
          items={[
            { label: 'offene Vorgänge', value: kennzahlen.offeneVorgaenge },
            { label: 'offenes Volumen', value: euro(kennzahlen.offenerWert) },
            { label: 'gewichtete Prognose', value: euro(kennzahlen.gewichteterWert) },
            { label: 'gewonnen', value: kennzahlen.gewonnen, tone: 'good', hint: euro(kennzahlen.gewonnenerWert) },
            { label: 'verloren', value: kennzahlen.verloren },
          ]}
        />
      )}

      {fehler && <div className="alert alert-error">{fehler}</div>}

      {zeigeNeu && darfBearbeiten && (
        <div className="quick-form" role="form" aria-label="Neuer Vorgang">
          <label>
            Titel
            <input autoFocus type="text" placeholder="z. B. Pentest Q4" value={neuTitel} onChange={(e) => setNeuTitel(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') anlegen(); }} />
          </label>
          <label>
            Firma
            <input type="text" placeholder="Name der Firma" value={neuFirma} onChange={(e) => setNeuFirma(e.target.value)} />
          </label>
          <label>
            Wert in EUR
            <input type="number" min={0} placeholder="0" value={neuWert} onChange={(e) => setNeuWert(e.target.value)} />
          </label>
          <div className="quick-form__actions">
            <button type="button" className="btn btn-primary" onClick={anlegen} disabled={!neuTitel.trim() || busy}>Anlegen</button>
            <button type="button" className="btn btn-ghost" onClick={() => setZeigeNeu(false)}>Abbrechen</button>
          </div>
          <p className="form-helper quick-form__hint">
            Tipp: Aus der <Link to="/companies">Firmenakte</Link> heraus angelegte Vorgänge sind automatisch mit der Firma verknüpft.
          </p>
        </div>
      )}

      {laedt ? (
        <LoadingState rows={4} label="Pipeline wird geladen" />
      ) : stufen.length === 0 ? (
        <EmptyState icon="kanban" title="Keine Pipeline-Stufen" description="Beim ersten Laden legt der Server die Standardstufen an. Bitte neu laden." action={{ label: 'Neu laden', onClick: laden }} />
      ) : (
        <div className="pipeline-board">
          {offeneStufen.map((stufe) => {
            const karten = vorgaenge.filter((d) => d.stageId === stufe.id);
            const summe = karten.reduce((s, d) => s + (d.value || 0), 0);
            return (
              <div
                key={stufe.id}
                className={`pipeline-spalte${zielStufe === stufe.id ? ' pipeline-spalte--ziel' : ''}`}
                // Ohne Schreibrecht keine Ablageflaeche: die Spalte nimmt nichts an.
                onDragOver={darfBearbeiten ? (e) => { e.preventDefault(); setZielStufe(stufe.id); } : undefined}
                onDragLeave={darfBearbeiten ? () => setZielStufe(null) : undefined}
                onDrop={darfBearbeiten ? (e) => {
                  e.preventDefault();
                  if (gezogen) verschieben(gezogen, stufe.id);
                  setGezogen(null);
                  setZielStufe(null);
                } : undefined}
              >
                <div className="pipeline-spalte-kopf">
                  <strong>{stufe.name}</strong>
                  <span className="pipeline-spalte-meta">
                    {karten.length} · {euro(summe)} · {stufe.probability} %
                  </span>
                </div>

                {karten.map((deal) => {
                  const f = faelligkeit(deal.nextStepAt);
                  return (
                    <div
                      key={deal.id}
                      className={`deal-karte ${istUeberfaellig(deal.nextStepAt) ? 'deal-karte-faellig' : ''}`}
                      draggable={darfBearbeiten}
                      role="button"
                      tabIndex={0}
                      onDragStart={darfBearbeiten ? () => setGezogen(deal.id) : undefined}
                      onDragEnd={darfBearbeiten ? () => setGezogen(null) : undefined}
                      onClick={() => oeffnen(deal)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); oeffnen(deal); } }}
                    >
                      <div className="deal-karte-titel">{deal.title}</div>
                      {deal.company && <div className="deal-karte-firma">{deal.company}</div>}
                      <div className="deal-karte-fuss">
                        <span>{euro(deal.value, deal.currency)}</span>
                        {f && <span className={f.ueberfaellig ? 'faellig' : ''}>{f.text}</span>}
                      </div>
                      {deal.nextStep && <div className="deal-karte-schritt">{deal.nextStep}</div>}
                    </div>
                  );
                })}

                {karten.length === 0 && <p className="pipeline-leer">{darfBearbeiten ? 'Hierher ziehen' : 'Kein Vorgang'}</p>}
              </div>
            );
          })}
        </div>
      )}

      {!laedt && vorgaenge.length === 0 && stufen.length > 0 && (
        <EmptyState
          icon="kanban"
          title="Noch kein offener Vorgang"
          description="Vorgänge entstehen aus Antworten mit Interesse („In die Pipeline“), aus der Firmenakte oder hier von Hand."
          action={darfBearbeiten ? { label: 'Vorgang anlegen', onClick: () => setZeigeNeu(true) } : undefined}
          secondaryAction={{ label: 'Zu den Antworten', to: '/outreach-inbox' }}
        />
      )}

      <Modal open={Boolean(offenerVorgang)} onOpenChange={(o) => { if (!o) schliessen(); }}>
        {offenerVorgang && (
          <ModalContent className="detail-modal">
            <ModalHeader>
              <ModalTitle>{offenerVorgang.title}</ModalTitle>
              <ModalDescription>
                {offenerVorgang.stage?.name ?? stufen.find((s) => s.id === offenerVorgang.stageId)?.name}
                {' · '}{euro(offenerVorgang.value, offenerVorgang.currency)}
                {offenerVorgang.status !== 'open' && <> · <StatusBadge status={offenerVorgang.status === 'won' ? 'done' : 'failed'}>{offenerVorgang.status === 'won' ? 'gewonnen' : 'verloren'}</StatusBadge></>}
                {' · '}angelegt {datum(offenerVorgang.createdAt)}
              </ModalDescription>
            </ModalHeader>
            <ModalBody>
              <div className="detail-modal__actions">
                {offenerVorgang.companyId && (
                  <Link to={`/companies/${offenerVorgang.companyId}`} className="btn btn-secondary btn-sm">
                    <Icon name="building" size={14} /> Firmenakte
                  </Link>
                )}
                {!offenerVorgang.companyId && offenerVorgang.company && (
                  <Link to={`/companies?suche=${encodeURIComponent(offenerVorgang.company)}`} className="btn btn-secondary btn-sm">
                    <Icon name="building" size={14} /> Firma suchen
                  </Link>
                )}
                {darfBearbeiten && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={aufgabeAnlegen}>
                    <Icon name="check" size={14} /> Aufgabe
                  </button>
                )}
                {darfAngebot && (
                  <button type="button" className="btn btn-secondary btn-sm" onClick={angebotErstellen} disabled={busy}>
                    <Icon name="file-check" size={14} /> Angebot erstellen
                  </button>
                )}
                {darfBearbeiten && offenerVorgang.status === 'open' && abschlussStufen.length > 0 && (
                  <>
                    <button type="button" className="btn btn-success btn-sm" onClick={() => abschliessen(true)}>Gewonnen</button>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => abschliessen(false)}>Verloren</button>
                  </>
                )}
                {darfLoeschen && (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={loeschen} title="Vorgang löschen">
                    <Icon name="trash" size={14} />
                  </button>
                )}
              </div>
              <RechteHinweis was="Vorgang bearbeiten und Notizen schreiben" />

              <div className="deal-detail">
                <label>
                  Firma
                  <input type="text" defaultValue={offenerVorgang.company || ''} disabled={!darfBearbeiten} onBlur={(e) => feldSpeichern('company', e.target.value)} />
                </label>
                <label>
                  Wert
                  <input type="number" defaultValue={offenerVorgang.value} disabled={!darfBearbeiten} onBlur={(e) => feldSpeichern('value', Number(e.target.value))} />
                </label>
                <label>
                  Stufe
                  <select value={offenerVorgang.stageId} disabled={!darfBearbeiten} onChange={(e) => feldSpeichern('stageId', e.target.value)}>
                    {stufen.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </label>
                <label>
                  Nächster Schritt
                  <input type="text" defaultValue={offenerVorgang.nextStep || ''} disabled={!darfBearbeiten} onBlur={(e) => feldSpeichern('nextStep', e.target.value)} />
                </label>
                <label>
                  fällig am
                  <input type="date" defaultValue={isoTag(offenerVorgang.nextStepAt)} disabled={!darfBearbeiten} onBlur={(e) => feldSpeichern('nextStepAt', e.target.value || null)} />
                </label>
                {offenerVorgang.lead && (
                  <p className="deal-detail-lead">
                    Kontakt:{' '}
                    <Link to={`/lead-sync?lead=${encodeURIComponent(offenerVorgang.lead.id)}`} title="In Leads & Outreach öffnen">
                      {[offenerVorgang.lead.firstName, offenerVorgang.lead.lastName].filter(Boolean).join(' ') || offenerVorgang.lead.email}
                    </Link>
                    {' · '}<a href={`mailto:${offenerVorgang.lead.email}`}>{offenerVorgang.lead.email}</a>
                  </p>
                )}
                {(offenerVorgang.lead?.lemlistCampaignId || offenerVorgang.source) && (
                  <div className="deal-detail-lead">
                    Herkunft:{' '}
                    <KampagneVerweis lemlistId={offenerVorgang.lead?.lemlistCampaignId} name={offenerVorgang.source} zuordnung={zuordnung} />
                  </div>
                )}
                {vorgangsVerweise(offenerVorgang).length > 0 && (
                  <div className="deal-detail-lead">
                    <RelatedLinks items={vorgangsVerweise(offenerVorgang)} label={`Verknüpft mit ${offenerVorgang.title}`} />
                  </div>
                )}
                {offenerVorgang.lostReason && (
                  <p className="deal-detail-lead">Verloren: {offenerVorgang.lostReason}</p>
                )}
              </div>

              <h3>Verlauf</h3>
              {darfBearbeiten && (
                <div className="notiz-eingabe">
                  <textarea
                    className="form-textarea"
                    rows={3}
                    placeholder="Notiz, Gesprächsergebnis, nächster Schritt …"
                    value={notiz}
                    onChange={(e) => setNotiz(e.target.value)}
                  />
                  <button type="button" className="btn btn-primary btn-sm" onClick={notizSpeichern} disabled={!notiz.trim()}>
                    Notiz speichern
                  </button>
                </div>
              )}

              <ul className="notiz-liste">
                {(offenerVorgang.notes || []).map((n) => (
                  <li key={n.id}>
                    <span className="notiz-datum">{zeit(n.createdAt)}</span>
                    <StatusBadge tone={n.kind === 'stage_change' ? 'info' : 'neutral'}>{NOTIZ_ART[n.kind] ?? n.kind}</StatusBadge>
                    <div className="notiz-text">{n.text}</div>
                  </li>
                ))}
                {(offenerVorgang.notes || []).length === 0 && <li className="text-sm text-secondary">Noch kein Eintrag.</li>}
              </ul>
            </ModalBody>
            <ModalFooter>
              <button type="button" className="btn btn-secondary" onClick={schliessen}>Schließen</button>
            </ModalFooter>
          </ModalContent>
        )}
      </Modal>
    </div>
  );
};

export default PipelinePage;
